import { notesDebounceMs } from './notes-debounce';
import { formDataServiceModule } from '../../../../../shared/app/services/form-data-service/form-data-service';
import { formDataComponentModule } from '../../../../../shared/app/components/forms/form-data/form-data';
import {
  SOLUTION_FORM_DATA_IGNORED_PROPERTIES,
  solutionModelModule,
} from '../../../../../shared/app/models/solution';
import { firebaseServiceModule } from '../../../../../shared/app/services/firebase/firebase';
import { solutionsServiceModule } from '../../../services/solutions-service/solutions-service';
import { unableToSaveDialogComponentModule } from '../../../components/unable-to-save-dialog/unable-to-save-dialog';
import { uiRouterStateModule } from '../../../../../shared/app/vendor/ui-router-module';
import { keycodesModule } from '../../../../../shared/app/constants/keycodes';
import { embedded } from '../../../config/embedded';
import { trackerServiceModule } from '../../../../../shared/app/services/tracker-service/tracker-service';
import { solutionAutosaveHelperModule } from '../../../../../shared/app/helpers/solution-autosave-helper/solution-autosave-helper';

const MAX_SAVE_FAILS = 5;

const TAG_STREAM_BUFFER_LENGTH = 100;

// When these events are matched in the stream, then we track an additional event
// The stream is stored in time order (the oldest event is at the start of the string)
const TAG_STREAM_EVENTS = [
  // external edits are a copy, followed by losing focus, followed by an external paste
  // We allow for multiple window blur/focus events, in case the candidate is flipping between the WebIDE and their
  // editor
  {
    matcher: /copy(-windowBlur|-windowFocus)+(-externalPaste|-largePaste)$/,
    tag: 'externalEdit',
  },
];

export const challengeProcessHelperModule = angular
  .module('Q.routes.assessments.challenges.challenge-process-helper', [
    uiRouterStateModule,
    solutionModelModule,
    solutionsServiceModule,
    formDataServiceModule,
    firebaseServiceModule,
    formDataComponentModule,
    unableToSaveDialogComponentModule,
    keycodesModule,
    trackerServiceModule,
    solutionAutosaveHelperModule,
  ])

  .factory(
    'challengeProcessHelper',
    function (
      $q,
      Solution,
      formDataService,
      formDataRestorePrompt,
      firebase,
      solutionsService,
      unableToSaveDialog,
      KeyCodes,
      $timeout,
      $state,
      appAlert,
      tracker,
      solutionAutosaveHelper,
    ) {
      /**
       * Provides basic shared functionality between different types of challenge processes.
       */
      return function (process, assessmentResultProcess) {
        let activeChallengeIdRef;
        let activeChallengeIdRefUnwatch;

        process.beginChallengeKeyStroke = `${KeyCodes.SYMBOLS.COMMAND_KEY} + ${KeyCodes.SYMBOLS.ENTER}`;

        process.startRun = (type) => {
          process.running = true;
          if (type) {
            firebase.set(process.firebaseRef + '/runData/runType', type);
          }
          return firebase.set(process.firebaseRef + '/runData/running', true);
        };
        process.endRun = () => {
          process.running = false;
          firebase.set(process.firebaseRef + '/runData/running', false);
        };

        assessmentResultProcess.liveInterview
          .setActiveChallengeId(process.challenge.id)
          .then((active) => {
            activeChallengeIdRef = active;
            activeChallengeIdRefUnwatch = activeChallengeIdRef.$watch(() => {
              if (
                process.passive &&
                !embedded &&
                activeChallengeIdRef.$value &&
                activeChallengeIdRef.$value !== process.challenge.id &&
                $state.params.challengeId === process.challenge.id
              ) {
                // we're in the background, and the candidate switched to another challenge, so
                // we're going to a safe place, the welcome screen
                appAlert.info(
                  `We've detected changes in a different tab or window. Please only work on your solutions in a single tab.`,
                  0,
                );
                $state.go('assessments.welcome');
              }
            });
          });

        function _saveSuccess(resp) {
          process.saveFails = 0;
          return resp;
        }

        function _saveFail(resp) {
          process.saveFails++;
          if (process.saveFails >= MAX_SAVE_FAILS) {
            // don't log network / connection errors
            if (
              !resp ||
              ![-1, 502, 504, 520, 521, 523, 524, 525, 526, 527].includes(
                resp.status,
              )
            ) {
              const assessmentResult = assessmentResultProcess.assessmentResult;
              const assessment = assessmentResultProcess.assessment;
              tracker.trackError('Save Solution Failed', {
                id: process.solution.id,
                value: process.saveFails, // for GA
                numberOfFailedAttempts: process.saveFails,
                challengeId: process.challenge.id,
                solutionId: process.solution.id,
                assessmentId: assessmentResult.assessmentId,
                assessmentResultId: assessmentResult.id,
                assessmentTitle: assessment.title,
                teamCandidateId: assessmentResult.teamCandidateId,
                teamId: assessmentResult.teamId,
                team: assessment.team && assessment.team.name,
                respData: resp && resp.data,
                respConfig: resp && resp.config,
                // TODO: remove for security reasons later?
                respHeaders: resp && resp.headers && resp.headers(),
              });
            }
            unableToSaveDialog.open(
              process.saveFails,
              process.resaveAfterFail,
              resp,
            );
          }
          return $q.reject(resp);
        }

        process.hasEdited = false;
        process.initializing = true;

        Object.defineProperties(process, {
          originalSolution: {
            get() {
              return (this.fd && this.fd.originalData) || null;
            },
          },
          solution: {
            get() {
              return (this.fd && this.fd.data) || null;
            },
            // noinspection JSUnusedLocalSymbols
            // eslint-disable-next-line no-unused-vars
            set(v) {
              /* ignored, can be triggered by ngModel */
            },
          },
          readOnly: {
            get() {
              return !!(
                assessmentResultProcess.readOnly ||
                assessmentResultProcess.background ||
                process.initializing ||
                process.usingAttach
              );
            },
          },
          restricted: {
            get() {
              return this.readOnly || assessmentResultProcess.restricted;
            },
          },
          /**
           * Passive means we accept any changes from the server, since we either are in read-only mode,
           * or have not made any changes to our code.
           */
          passive: {
            get() {
              // passive is true if we're in readOnly mode, and we don't have unsaved edits
              return this.readOnly && !process.hasEdited;
            },
          },
          files: {
            get() {
              return (this.solution && this.solution.files) || [];
            },
          },
        });

        function _afterSaveOrUpdate(force) {
          if (process.cleanupAfterSave) process.cleanupAfterSave(force);
          process.solution.updateScore();
          assessmentResultProcess.refreshStageStatuses();
          if ($state.activeParams.challengeId === process.challenge.id) {
            assessmentResultProcess.postUpdated(process.challenge);
          }
        }

        let notesTimer;

        let lastCopy = null;
        let tagStream = [];

        angular.extend(process, {
          firebaseRef:
            assessmentResultProcess.liveInterview.firebaseRef +
            '/' +
            process.challenge.id,
          saveFails: 0,

          autosave(tag) {
            if (
              assessmentResultProcess.restricted ||
              (process.readOnly && !process.usingAttach)
            ) {
              return $q.when(process.solution);
            }
            const assessment = assessmentResultProcess.assessment;

            const savePromise = solutionAutosaveHelper({
              tag,
              fd: process.fd,
              team: assessment.team,
              solution: process.originalSolution,
              cursor: process.cursor,
              interact: process.interact,
            });
            if (savePromise) {
              process.hasEdited = true;
              return savePromise
                .then(() => {
                  // keep a history of recent tags, ignoring duplicate tags and generic save events
                  if (
                    tag &&
                    tag !== 'autosave' &&
                    tagStream[tagStream.length - 1] !== tag
                  ) {
                    tagStream.push(tag);
                    // cap tag stream length
                    while (tagStream.length > TAG_STREAM_BUFFER_LENGTH) {
                      tagStream.shift();
                    }
                  }
                  _afterSaveOrUpdate();
                  return process.solution;
                })
                .then(_saveSuccess, _saveFail)
                .then((result) => {
                  // TODO: this would be better if we could push up multiple tags
                  // check for a known tagStream event and save it if we have one
                  const tsString = tagStream.join('-');
                  const tsEvent = TAG_STREAM_EVENTS.find((tsEvent) =>
                    tsEvent.matcher.test(tsString),
                  );
                  if (tsEvent) {
                    return process.autosave(tsEvent.tag);
                  } else {
                    return result;
                  }
                });
            } else {
              return $q.when(process.solution);
            }
          },

          onCopy(copied) {
            // save that the user copied code
            $timeout(() => process.autosave('copy'));
            lastCopy = copied.trim();
          },

          onPaste(file, pasted) {
            pasted = pasted.trim();
            // only store paste events if the paste came from outside the editor & the file is an editor file (ignore comments, etc)
            if (process.files.includes(file)) {
              let tag = 'paste';
              // let externalEdit = false;
              if (pasted !== lastCopy) {
                tag = pasted.length > 50 ? 'largePaste' : 'externalPaste';
              }
              $timeout(() => process.autosave(tag));
            }
          },

          initSolution(solution) {
            return assessmentResultProcess
              .startAssessmentResult()
              .then(() => {
                if (process.challenge.isTimed() && solution.pendingTimerReset) {
                  return Solution.resetTimer(solution);
                } else {
                  return solution;
                }
              })
              .then((sol) => {
                if (process.challenge.isTimed()) {
                  process.timerStartedAt = sol.timerStartedAt;
                  process.timerTimeoutSeconds =
                    process.challenge.timeLimitMin * 60;
                }
                process.fd = formDataService.create({
                  originalData: sol,
                  ignoredProperties: SOLUTION_FORM_DATA_IGNORED_PROPERTIES,
                  warnOnUnload: false,
                });
                // enable typed methods & actions directly on the solution
                process.fd.data.$asType();
                if (
                  !assessmentResultProcess.readOnly &&
                  !process.solution.expired()
                ) {
                  formDataRestorePrompt
                    .prompt(process.fd, 'this solution')
                    .then(() => {
                      if (process.afterInitSolution) {
                        process.afterInitSolution();
                      }
                      process.autosave();
                    });
                }
              });
          },

          onEnter($scope, extraModels = []) {
            $scope.$on('document:focus', () => {
              if (process.fd) {
                // try to quietly revert to the last saved version when focusing
                process.originalSolution
                  .DSRefresh({ ignoreLoadingBar: true })
                  .then(() => {
                    if (!process.hasEdited) {
                      // try to reset the editor, but only if the candidate hasn't edited anything yet
                      process.fd.syncUnchanged();
                      _afterSaveOrUpdate();
                    }
                    // trigger a save of re-focusing
                    process.autosave('windowFocus');
                    // make sure we continue to accept server edits
                    process.hasEdited = false;
                  });
              }
            });
            $scope.$on('document:blur', () => {
              process.autosave('windowBlur');
              process.hasEdited = false;
              process.background = true;
            });
            const updateChanges = () => {
              $timeout(() => {
                if (process.passive && process.fd) {
                  if (!process.interact) {
                    process.fd.reset();
                    _afterSaveOrUpdate(true);
                  } else if (!process.hasEdited) {
                    // try to reset the editor, but only if the candidate hasn't edited anything yet
                    process.fd.syncUnchanged();
                    _afterSaveOrUpdate();
                  }
                }
              }, 1);
            };
            $scope.$on(`socket:model.changed:Solution`, updateChanges);
            extraModels.forEach((model) => {
              $scope.$on(`socket:model.changed:${model}`, updateChanges);
            });
            process.initializing = false;

            assessmentResultProcess.postUpdated(process.challenge);
          },

          onExit() {
            if (activeChallengeIdRefUnwatch) {
              activeChallengeIdRefUnwatch();
            }
            if (
              activeChallengeIdRef &&
              activeChallengeIdRef.$value === process.challenge.id
            ) {
              // clears the current active challenge ID reference, without going through the async route,
              // otherwise we might accidentally clear it after the user switches to a new section
              activeChallengeIdRef.$value = null;
              activeChallengeIdRef.$save();
            }
            $timeout.cancel(notesTimer);
            process.autosave();
            if (process.fd) {
              process.fd.close();
            }
          },

          _saveSuccess,
          _saveFail,

          mfcChallengeConfig() {
            return [
              {
                id: 'instructions',
                icon: 'info',
                label: 'Instructions',
                template: 'instructionsViewer',
                challenge: process.challenge,
                get language() {
                  return process.language;
                },
              },
              {
                id: 'candidatenotes',
                icon: 'note',
                label: 'Your Notes',
                template: 'markdown',
                alwaysEdit: true,
                get contents() {
                  return process.solution.candidateNotes;
                },
                set contents(val) {
                  const delay = notesDebounceMs({
                    scored:
                      process.challenge.candidateNotesScored &&
                      process.solution.isClassicCode(),
                    timed: process.challenge.isTimed(),
                    timerStartedAt: process.solution.timerStartedAt,
                    timeLimitMin: process.challenge.timeLimitMin,
                  });
                  // the form value stays live (so it survives a reload); only the save is debounced
                  process.solution.candidateNotes = val;
                  if (delay === null) return;
                  // trailing debounce so scored notes don't write a revision per keystroke;
                  // a 0 delay (time limit passed) saves right away. A timer that outlives a
                  // tagged save is a no-op because the form has no changes left.
                  $timeout.cancel(notesTimer);
                  notesTimer = $timeout(() => process.autosave(), delay);
                },
                get readOnly() {
                  return process.solution.notesLocked();
                },
                get notice() {
                  return process.solution.notesLocked()
                    ? 'The time limit has passed, so your notes can no longer be edited.'
                    : null;
                },
                placeholder: process.challenge.candidateNotesPlaceholder(),
              },
            ];
          },
        });

        // Initialize the existing solution if we have one
        const sol = solutionsService.get(
          process.challenge,
          assessmentResultProcess.assessmentResult,
        );

        if (sol) {
          return process
            .initSolution(sol)
            .catch(appAlert.modelError)
            .then(() => process);
        } else {
          return $q.resolve(process);
        }
      };
    },
  );
